/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useMemo, useState, useEffect } from 'react';
import { Combobox, ComboboxInput } from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { useEvents } from '../../hooks/useEvents';
import EventItem, { EventItemHeader } from './EventItem';
import { Link, useSearchParams } from 'react-router-dom';
import { type IEvent } from '../../reducers/eventsSlice';
import { ClockIcon } from '@heroicons/react/16/solid';

// Filter types
type StatusFilter = 'for-review' | 'rejected' | 'ready' | 'pending' | 'decommissioned';
type SpecialFilter = 'starting-soon' | 'decommissioning-soon';
type FilterType = StatusFilter | SpecialFilter;

interface FilterButtonProps {
  label: string;
  icon?: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
}

const FilterButton: React.FC<FilterButtonProps> = ({ label, icon = undefined, isActive, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`flex items-center px-2 py-0.5 gap-1 rounded-sm text-sm font-medium transition-colors ${
        isActive ? 'bg-dataops-rich-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {icon && <div>{icon}</div>}
      <div>{label}</div>
    </button>
  );
};

// Filter configuration
const FILTER_CONFIG: Array<{
  key: FilterType;
  label: string;
  icon?: React.ReactNode;
}> = [
  {
    key: 'for-review',
    label: 'For Review',
    icon: <div className="h-2 w-2 bg-dataops-saffron-yellow rounded-full" />,
  },
  { key: 'rejected', label: 'Rejected', icon: <div className="h-2 w-2 bg-dataops-crayola-red rounded-full" /> },
  { key: 'ready', label: 'Ready', icon: <div className="h-2 w-2 bg-dataops-forest-green rounded-full" /> },
  { key: 'pending', label: 'Pending', icon: <div className="h-2 w-2 bg-dataops-taupe-gray rounded-full" /> },
  {
    key: 'decommissioned',
    label: 'Decommissioned',
    icon: <div className="h-2 w-2 bg-dataops-rich-black rounded-full" />,
  },
  { key: 'starting-soon', label: 'Starting Soon', icon: <ClockIcon className="h-3 w-3 text-dataops-amethyst-lilac" /> },
  {
    key: 'decommissioning-soon',
    label: 'Decommissioning Soon',
    icon: <ClockIcon className="h-3 w-3 text-dataops-amethyst-lilac" />,
  },
];

export default function EventsList() {
  const { events } = useEvents();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Set<FilterType>>(new Set());

  // Initialize search value and filters from URL params
  useEffect(() => {
    setSearchValue(searchParams.get('search') ?? '');

    // Initialize filters from URL params
    const filtersParam = searchParams.get('filters');
    if (filtersParam) {
      const filters = filtersParam.split(',') as FilterType[];
      setActiveFilters(new Set(filters));
    }
  }, [searchParams]);

  // Debounce search value
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 300);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchValue]);

  // Handle search input change
  function onSearchInputChange(value: string) {
    setSearchValue(value);
    updateUrlParams(value, activeFilters);
  }

  // Handle filter toggle
  function toggleFilter(filter: FilterType) {
    const newFilters = new Set(activeFilters);
    if (newFilters.has(filter)) {
      newFilters.delete(filter);
    } else {
      newFilters.add(filter);
    }
    setActiveFilters(newFilters);
    updateUrlParams(searchValue, newFilters);
  }

  // Update URL params with search and filters
  function updateUrlParams(search: string, filters: Set<FilterType>) {
    const params: Record<string, string> = {};

    if (search.trim()) {
      params.search = search;
    }

    if (filters.size > 0) {
      params.filters = Array.from(filters).join(',');
    }

    setSearchParams(params);
  }

  // Helper function to get event status for filtering
  function getEventStatus(event: IEvent): StatusFilter {
    const accountPool = event.account_pool;
    const isDecommissioned =
      accountPool &&
      ['pending-decommissioning', 'decommissioning', 'decommissioned'].includes(accountPool.status.toLowerCase());

    switch (event.approval_status) {
      case 'PENDING':
        return 'for-review';
      case 'REJECTED':
        return 'rejected';
      case 'APPROVED':
        if (isDecommissioned) {
          return 'decommissioned';
        }
        if (accountPool && accountPool.status.toLowerCase() === 'ready') {
          return 'ready';
        }
        return 'pending';
      default:
        return 'for-review';
    }
  }

  // Helper function to check if event is starting soon
  function isStartingSoon(event: IEvent): boolean {
    const now = new Date();
    const buildDate = new Date(event.build_datetime);
    const startDate = new Date(event.start_datetime);

    // Event is starting soon if it's scheduled to start and after the build date
    return startDate > now && buildDate <= now;
  }

  // Helper function to check if event is decommissioning soon
  function isDecommissioningSoon(event: IEvent): boolean {
    const now = new Date();
    const endDate = new Date(event.end_datetime);
    const decommissionDate = new Date(event.decommission_datetime);

    // Event is decommissioning soon if it's scheduled for decommissioning and after the end date
    return decommissionDate > now && endDate <= now;
  }

  // Filter and sort all events based on search and filters
  const filteredEvents = useMemo(() => {
    const eventList = Array.isArray(events) ? events : [];

    // Apply search filter if there's a search term
    let searchFilteredEvents = eventList;
    if (debouncedSearchValue.trim()) {
      const searchTerm = debouncedSearchValue.toLowerCase();
      searchFilteredEvents = eventList.filter(
        (event) => event.name?.toLowerCase().includes(searchTerm) || event.slug?.toLowerCase().includes(searchTerm),
      );
    }

    // Apply status and special filters
    let statusFilteredEvents = searchFilteredEvents;
    if (activeFilters.size > 0) {
      statusFilteredEvents = searchFilteredEvents.filter((event) => {
        // Check status filters
        const eventStatus = getEventStatus(event);
        const hasStatusFilter = activeFilters.has(eventStatus);

        // Check special filters
        const hasStartingSoonFilter = activeFilters.has('starting-soon') && isStartingSoon(event);
        const hasDecommissioningSoonFilter = activeFilters.has('decommissioning-soon') && isDecommissioningSoon(event);

        // Event passes if it matches any active filter
        return hasStatusFilter || hasStartingSoonFilter || hasDecommissioningSoonFilter;
      });
    }

    // Create a copy of the array before sorting to avoid mutating the original
    // Sort by: 1) status priority, 2) start date, 3) project name
    return [...statusFilteredEvents].sort((a, b) => {
      // Define status priority order
      const statusPriority: Record<StatusFilter, number> = {
        'for-review': 1,
        ready: 2,
        pending: 3,
        rejected: 4,
        decommissioned: 5,
      };

      // 1. Sort by status priority
      const statusA = getEventStatus(a);
      const statusB = getEventStatus(b);
      const statusComparison = statusPriority[statusA] - statusPriority[statusB];

      if (statusComparison !== 0) {
        return statusComparison;
      }

      // 2. Sort by start date (earliest first)
      const startDateA = new Date(a.start_datetime).getTime();
      const startDateB = new Date(b.start_datetime).getTime();
      const dateComparison = startDateA - startDateB;

      if (dateComparison !== 0) {
        return dateComparison;
      }

      // 3. Sort by project name (alphabetical)
      const nameA = a.name?.toLowerCase() || '';
      const nameB = b.name?.toLowerCase() || '';
      return nameA.localeCompare(nameB);
    });
  }, [events, debouncedSearchValue, activeFilters]);

  // Render search box component
  const renderSearchBox = () => (
    <div className="flex items-center gap-6">
      <div className="grow">
        <div className="flex flex-row gap-2">
          <div className="h-[33px] relative flex-1 rounded-md bg-dataops-supporting-gray/50">
            <MagnifyingGlassIcon
              className="pointer-events-none absolute left-3 top-2 h-5 w-5 text-gray-500"
              aria-hidden="true"
            />
            <Combobox>
              <ComboboxInput
                value={searchValue}
                aria-label="search-events-input"
                placeholder="Type to search events by name or slug"
                className="h-[33px] w-full border border-gray-200 bg-transparent pl-11 pr-4 text-gray-900 placeholder:text-gray-500 ring-0 focus:ring-0 sm:text-sm rounded-md outline-none outline-offset-0 focus:outline focus:outline-dataops-primary-light-blue/20 focus:outline-4 transition-all ease-in-out"
                data-testid="search-events-input"
                onChange={(event) => {
                  onSearchInputChange(event.target.value);
                }}
              />
            </Combobox>
          </div>
        </div>
      </div>
    </div>
  );

  // Render filter buttons component
  const renderFilterButtons = () => (
    <div className="flex flex-wrap gap-2">
      {FILTER_CONFIG.map(({ key, label, icon }) => (
        <FilterButton
          key={key}
          label={label}
          icon={icon}
          isActive={activeFilters.has(key)}
          onClick={() => {
            toggleFilter(key);
          }}
        />
      ))}
    </div>
  );

  // Render content based on state
  const renderContent = () => {
    if (debouncedSearchValue.trim() && filteredEvents.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-500">No events found matching &ldquo;{debouncedSearchValue}&rdquo;</p>
        </div>
      );
    }

    if (filteredEvents.length > 0) {
      return (
        <div className="">
          <EventItemHeader />
          <ul role="list" className="w-full space-y-1">
            {filteredEvents.map((event) => (
              <li key={event.slug}>
                <Link to={`/event-deployments/${event.slug}`}>
                  <EventItem event={event} />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      );
    }

    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No events available</p>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {renderSearchBox()}
      {renderFilterButtons()}
      {renderContent()}
    </div>
  );
}
